import React, { useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import DownCaret from "../../../../assets/DownCaret.svg";
import RightCaret from "../../../../assets/RightCaret.svg";
import { selectUser } from "../../../../store/slices/authentication";
import {
  useUpdateBulletinPostMutation,
  useDeleteBulletinPostMutation,
  useToggleBulletinReactionMutation,
} from "../../../../store/slices/api";
import { SharedTextInput, SharedButton } from "../../../shared";
import { failedMessage } from "../../../shared/serverReason";
import FileUploader from "../CreatePost/FileUploader";
import ImageLightbox from "./ImageLightbox";
import {
  CATEGORIES,
  CATEGORY_COLORS,
  categoryLabel,
  normalizeCategory,
} from "../categories";

dayjs.extend(utc);
dayjs.extend(timezone);

const MAX_TITLE_LENGTH = 100;
const MAX_CONTENT_LENGTH = 5000;
const MAX_PHONE_NUMBER_LENGTH = 20;
const MAX_EMAIL_LENGTH = 100;
const MAX_LOCATION_LENGTH = 100;

const SAVE_FAILED =
  "Your changes didn't save. Everything you changed is still here — please try again.";
const DELETE_FAILED = "The post wasn't deleted. Please try again.";

// True when the text inside `ref` is cut off by its line clamp. Measured, not
// guessed from the length: three short lines are cut just the same as one long
// paragraph, and a long line on a wide screen may not be cut at all.
function useIsClamped(ref, text, clamped) {
  const [isClamped, setIsClamped] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !clamped) return undefined;
    const measure = () => setIsClamped(el.scrollHeight > el.clientHeight + 1);
    measure();
    // Re-measure when the card changes width (a phone turned sideways).
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, text, clamped]);
  return isClamped;
}

function CategoryTag({ category }) {
  // Old stored values ("formal discussions", "coffee & catch-up") resolve to
  // their current name here, so nothing ever looks orphaned.
  const canonical = normalizeCategory(category);
  const colors = CATEGORY_COLORS[canonical] || CATEGORY_COLORS.other;
  const label = categoryLabel(category);
  return (
    <span
      className="text-[11px] font-bold px-[10px] py-[3px] rounded-full whitespace-nowrap"
      style={{ backgroundColor: colors.bg, color: colors.color }}
    >
      {label}
    </span>
  );
}

// The small set of taps the backend accepts (kept in sync with reaction.py).
const REACTION_EMOJIS = ["❤️", "🎉", "👍", "😂", "🙏"];

function ReactionBar({ post }) {
  const [toggleReaction, { isLoading }] = useToggleBulletinReactionMutation();
  const counts = post.reactions || {};
  const mine = post.my_reactions || [];
  return (
    <div className="flex flex-row flex-wrap gap-[8px]">
      {REACTION_EMOJIS.map((emoji) => {
        const count = counts[emoji] || 0;
        const reacted = mine.includes(emoji);
        return (
          <button
            key={emoji}
            type="button"
            disabled={isLoading}
            onClick={() => toggleReaction({ postId: post.id, emoji })}
            aria-pressed={reacted}
            className={`flex flex-row items-center gap-x-[5px] px-[10px] py-[3px] rounded-full border text-[13px] transition-colors disabled:opacity-50 ${
              reacted
                ? "border-dupontGold bg-dupontGold/15 text-dupontGoldDk dark:text-darkGold"
                : "border-dupontGold/30 hover:bg-dupontGold/10 text-dupontGray dark:text-darkTextSecondary"
            }`}
          >
            <span>{emoji}</span>
            {count > 0 && <span className="font-semibold">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

const DOC_EXTENSIONS = ["pdf", "doc", "docx", "xls", "xlsx", "txt", "csv", "zip", "ppt", "pptx"];

function getEffectiveType(attachment) {
  const ext = attachment.original_name?.split(".").pop()?.toLowerCase();
  if (DOC_EXTENSIONS.includes(ext)) return "raw";
  return attachment.resource_type;
}

// Only well-formed files reach the gallery. A hand-made post could hold a
// bare string, a null, a numeric name or a javascript: link, and one such post
// used to crash the whole Bulletin for everyone who opened it. (The server refuses them now;
// this also covers any already saved.)
function usableAttachments(attachments) {
  if (!Array.isArray(attachments)) return [];
  return attachments
    .filter((a) => a && typeof a === "object" && typeof a.url === "string" && /^https?:\/\//i.test(a.url.trim()))
    .map((a) => ({
      ...a,
      original_name: typeof a.original_name === "string" ? a.original_name : undefined,
      resource_type: typeof a.resource_type === "string" ? a.resource_type : undefined,
    }));
}

function MediaGallery({ attachments: saved }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const attachments = usableAttachments(saved);

  if (attachments.length === 0) return null;

  const images = attachments.filter((a) => getEffectiveType(a) === "image");
  const videos = attachments.filter((a) => getEffectiveType(a) === "video");
  const docs = attachments.filter((a) => {
    const t = getEffectiveType(a);
    return t !== "image" && t !== "video";
  });

  const getExtension = (name) => (name ? name.split(".").pop().toLowerCase() : "");
  const FILE_TYPE_BADGES = {
    pdf: { label: "PDF", bg: "#FEE2E2", color: "#991B1B" },
    doc: { label: "DOC", bg: "#DBEAFE", color: "#1E40AF" },
    docx: { label: "DOCX", bg: "#DBEAFE", color: "#1E40AF" },
    xls: { label: "XLS", bg: "#D1FAE5", color: "#065F46" },
    xlsx: { label: "XLSX", bg: "#D1FAE5", color: "#065F46" },
    txt: { label: "TXT", bg: "#F3F4F6", color: "#374151" },
    csv: { label: "CSV", bg: "#D1FAE5", color: "#065F46" },
  };

  return (
    <div className="flex flex-col gap-y-[10px]">
      {/* Images */}
      {images.length > 0 && (
        <div className="flex flex-row flex-wrap gap-[8px]">
          {/* Each photo is a button, so it opens full screen from the keyboard too. */}
          {images.map((img, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setLightboxIndex(idx)}
              aria-label={`Open photo ${idx + 1} of ${images.length} full screen`}
              className="block p-0 m-0 bg-transparent border-0 rounded-[10px] cursor-pointer"
            >
              <img
                src={img.url}
                alt={img.original_name}
                loading="lazy"
                decoding="async"
                className="block w-[140px] h-[140px] object-cover rounded-[10px] border border-dupontGold/40 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
              />
            </button>
          ))}
        </div>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <div className="flex flex-col gap-y-[8px]">
          {videos.map((vid, idx) => (
            <video
              key={idx}
              src={vid.url}
              controls
              className="max-w-full max-h-[300px] rounded-[10px] border border-dupontGold/40 shadow-sm"
            >
              Your browser does not support video playback.
            </video>
          ))}
        </div>
      )}

      {/* Documents */}
      {docs.length > 0 && (
        <div className="flex flex-row flex-wrap gap-[8px]">
          {docs.map((doc, idx) => {
            const ext = getExtension(doc.original_name);
            const badge = FILE_TYPE_BADGES[ext] || {
              label: ext ? ext.toUpperCase() : "FILE",
              bg: "#F3F4F6",
              color: "#374151",
            };
            return (
              <a
                key={idx}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-row items-center gap-x-[8px] px-[12px] py-[8px] rounded-[10px] border border-dupontGold/30 bg-white dark:bg-darkCard shadow-sm hover:shadow-md transition-shadow no-underline"
              >
                <span
                  className="text-[10px] font-bold px-[6px] py-[2px] rounded-full"
                  style={{ backgroundColor: badge.bg, color: badge.color }}
                >
                  {badge.label}
                </span>
                <span className="text-[13px] text-dupontBlue dark:text-darkText truncate max-w-[200px]">
                  {doc.original_name || "Attached file"}
                </span>
              </a>
            );
          })}
        </div>
      )}

      {/* Image lightbox */}
      {lightboxIndex !== null && (
        <ImageLightbox
          images={images.map((img) => img.url)}
          startIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

function PostCard({ post, isAdmin }) {
  const user = useSelector(selectUser);
  const [showContact, setShowContact] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);
  const contentRef = useRef(null);
  const isClamped = useIsClamped(contentRef, post.content, !showFullContent);

  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(post.title);
  const [editContent, setEditContent] = useState(post.content);
  // Normalize so a legacy stored value ("coffee & catch-up") preselects its
  // current name instead of showing blank. Saving then migrates that one post.
  const [editCategory, setEditCategory] = useState(
    normalizeCategory(post.category)
  );
  const [editAttachments, setEditAttachments] = useState(
    post.attachments || []
  );
  const [editPhone, setEditPhone] = useState(post.phone_number || "");
  const [editEmail, setEditEmail] = useState(post.email || "");
  const [editLocation, setEditLocation] = useState(post.location || "");
  // True while a photo or file is still uploading in the editor.
  const [editUploading, setEditUploading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [updateBulletinPost, { isLoading: isUpdating }] =
    useUpdateBulletinPostMutation();
  const [deleteBulletinPost, { isLoading: isDeleting }] =
    useDeleteBulletinPostMutation();

  const isOwner =
    user?.externalId && post.individual_id === parseInt(user.externalId);
  const canModify = isOwner || isAdmin;

  const getTimezoneAbbr = () => {
    const now = new Date();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timeZoneAbbr =
      new Intl.DateTimeFormat("en-US", {
        timeZone,
        timeZoneName: "short",
      })
        .formatToParts(now)
        .find((part) => part.type === "timeZoneName")?.value || "";
    return timeZoneAbbr;
  };

  const handleStartEdit = () => {
    setEditTitle(post.title);
    setEditContent(post.content);
    setEditCategory(normalizeCategory(post.category));
    setEditAttachments(post.attachments || []);
    setEditPhone(post.phone_number || "");
    setEditEmail(post.email || "");
    setEditLocation(post.location || "");
    setEditError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditError(null);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (isUpdating || editUploading) return;
    setEditError(null);
    // Only close the editor once the server has the changes. On failure the
    // editor stays open with every edit kept, and says why.
    try {
      await updateBulletinPost({
        id: post.id,
        individual_id: parseInt(user.externalId),
        title: editTitle,
        content: editContent,
        category: editCategory,
        attachments: editAttachments,
        phone_number: editPhone,
        email: editEmail,
        location: editLocation,
      }).unwrap();
    } catch (err) {
      setEditError(failedMessage(err, SAVE_FAILED));
      return;
    }
    setIsEditing(false);
  };

  const handleDelete = async () => {
    setDeleteError(null);
    try {
      await deleteBulletinPost({
        id: post.id,
        individual_id: parseInt(user.externalId),
      }).unwrap();
    } catch (err) {
      setDeleteError(failedMessage(err, DELETE_FAILED));
    }
  };

  // ---- EDITING VIEW ----
  if (isEditing) {
    return (
      <div className="flex flex-col rounded-[16px] shadow-xl dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-[16px] border border-dupontGold gap-y-[12px] bg-dupontIvory dark:bg-darkCard">
        <div className="text-[16px] font-bold text-dupontBlue dark:text-darkText">
          Editing Post
        </div>
        <SharedTextInput
          label="Title*"
          name="edit-title"
          value={editTitle}
          onChange={setEditTitle}
          variant="outlined"
          maxChars={MAX_TITLE_LENGTH}
        />
        <Autocomplete
          options={CATEGORIES}
          value={editCategory}
          onChange={(_, newValue) => setEditCategory(newValue || "other")}
          getOptionLabel={(option) => categoryLabel(option)}
          renderInput={(params) => (
            <TextField {...params} label="Category*" variant="outlined" />
          )}
          size="small"
        />
        <SharedTextInput
          label="Post Content*"
          name="edit-content"
          value={editContent}
          onChange={setEditContent}
          multiline
          rows={4}
          variant="outlined"
          maxChars={MAX_CONTENT_LENGTH}
        />
        <FileUploader
          attachments={editAttachments}
          onAttachmentsChange={setEditAttachments}
          onUploadingChange={setEditUploading}
        />
        <SharedTextInput
          label="Phone Number (optional)"
          name="edit-phone"
          value={editPhone}
          onChange={setEditPhone}
          variant="outlined"
          maxChars={MAX_PHONE_NUMBER_LENGTH}
        />
        <SharedTextInput
          label="Email"
          name="edit-email"
          value={editEmail}
          onChange={setEditEmail}
          variant="outlined"
          maxChars={MAX_EMAIL_LENGTH}
        />
        <SharedTextInput
          label="Location (optional)"
          name="edit-location"
          value={editLocation}
          onChange={setEditLocation}
          variant="outlined"
          maxChars={MAX_LOCATION_LENGTH}
        />
        {editError && (
          <div role="alert" className="text-[15px] font-body text-red-700 dark:text-red-400">
            {editError}
          </div>
        )}
        <div className="flex flex-row gap-x-[10px]">
          <SharedButton
            disabled={!editTitle || !editContent || editUploading}
            onClick={handleSaveEdit}
            loadingText={isUpdating ? "Saving..." : undefined}
          >
            Save Changes
          </SharedButton>
          <SharedButton color="secondary" onClick={handleCancelEdit}>
            Cancel
          </SharedButton>
        </div>
      </div>
    );
  }

  // ---- NORMAL VIEW ----
  return (
    <div className="flex flex-col rounded-[16px] shadow-xl dark:shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-[16px] border border-dupontGold bg-white dark:bg-darkCard gap-y-[12px]">
      {/* Header row: category tag + pinned badge + timestamp. On a phone the
          timestamp drops under the title instead of squeezing it. */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-x-[10px] gap-y-[4px]">
        <div className="flex flex-col gap-y-[6px] flex-1 min-w-0 w-full">
          <div className="flex flex-row items-center gap-x-[8px] flex-wrap">
            <CategoryTag category={post.category} />
            {post.is_pinned && (
              <span className="text-[11px] font-bold px-[10px] py-[3px] rounded-full bg-dupontGold/20 text-dupontGoldDk whitespace-nowrap">
                Pinned
              </span>
            )}
          </div>
          <div className="text-[18px] font-bold text-dupontBlue dark:text-darkText break-words [overflow-wrap:anywhere] min-w-0">
            {post.title}
          </div>
        </div>
        <div className="text-[13px] text-dupontGray dark:text-darkTextSecondary whitespace-nowrap sm:pt-[4px]">
          {dayjs(post.created_at).utc().local().format("MMM D, YYYY h:mma")} (
          {getTimezoneAbbr()})
        </div>
      </div>

      {/* Media gallery */}
      <MediaGallery attachments={post.attachments} />

      {/* Content */}
      <div className="text-[16px] dark:text-darkText">
        <div
          ref={contentRef}
          className={`${
            showFullContent ? "" : "line-clamp-2"
          } whitespace-pre-wrap break-words [overflow-wrap:anywhere]`}
        >
          {post.content}
        </div>
        {(isClamped || showFullContent) && (
          <button
            onClick={() => setShowFullContent(!showFullContent)}
            className="text-dupontBlue dark:text-darkGold text-[14px] font-medium mt-1 hover:underline"
          >
            {showFullContent ? "Show less" : "Show more"}
          </button>
        )}
      </div>

      {/* Contact info */}
      <div className="w-full border-t pt-[10px] border-dupontGold">
        <button
          type="button"
          aria-expanded={showContact}
          className="flex flex-row items-center gap-x-[10px] cursor-pointer w-fit text-left"
          onClick={() => setShowContact(!showContact)}
        >
          <span className="text-[16px] font-[500] dark:text-darkText">Contact Info</span>
          <img
            src={showContact ? DownCaret : RightCaret}
            alt=""
            className="w-[10px] h-[10px] brightness-0 dark:invert"
          />
        </button>
      </div>
      {/* A grid, not two separate columns, so a long email or place that wraps
          onto a second line keeps its label beside it. */}
      {showContact && (
        <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-[20px] gap-y-[3px] pl-[10px] dark:text-darkText">
          <div>Name:</div>
          <div className="font-[500] break-words min-w-0">{post.posted_by || "Unknown"}</div>
          <div>Email:</div>
          <div className="min-w-0">
            {post.email ? (
              <a
                href={`mailto:${post.email}`}
                className="text-dupontBlue dark:text-darkGold hover:underline cursor-pointer [overflow-wrap:anywhere]"
              >
                {post.email}
              </a>
            ) : (
              "No email provided"
            )}
          </div>
          {/* Phone and location are optional; a line is shown only when the
              poster chose to share it. */}
          {post.phone_number?.trim() && (
            <>
              <div>Phone:</div>
              <div className="break-words min-w-0">{post.phone_number}</div>
            </>
          )}
          {post.location?.trim() && (
            <>
              <div>Location:</div>
              <div className="break-words min-w-0 [overflow-wrap:anywhere]">{post.location}</div>
            </>
          )}
        </div>
      )}

      {/* Edit / Delete buttons */}
      {canModify && (
        <div className="flex flex-row gap-x-[10px] pt-[8px] border-t border-dupontGold/30 items-center">
          {isAdmin && !isOwner && (
            <span className="text-[11px] font-bold text-dupontGray dark:text-darkTextSecondary bg-dupontCreamDk dark:bg-darkElevated px-[8px] py-[2px] rounded-full mr-[4px]">
              Admin
            </span>
          )}
          {showDeleteConfirm ? (
            <div className="flex flex-row flex-wrap items-center gap-x-[10px]">
              <span className="text-[14px] text-red-600">
                Are you sure you want to delete this post?
              </span>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="text-[13px] font-bold text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
              <button
                onClick={() => {
                  setDeleteError(null);
                  setShowDeleteConfirm(false);
                }}
                className="text-[13px] font-bold text-dupontGray dark:text-darkTextSecondary hover:text-dupontBlue dark:hover:text-darkText"
              >
                Cancel
              </button>
              {deleteError && (
                <span role="alert" className="basis-full text-[14px] font-body text-red-700 dark:text-red-400">
                  {deleteError}
                </span>
              )}
            </div>
          ) : (
            <>
              <button
                onClick={handleStartEdit}
                className="text-[13px] font-bold text-dupontGoldDk hover:text-dupontGold"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="text-[13px] font-bold text-red-400 hover:text-red-600"
              >
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Reactions — bottom of the card, below edit/delete */}
      <div className="pt-[8px] border-t border-dupontGold/30">
        <ReactionBar post={post} />
      </div>
    </div>
  );
}

export { CategoryTag, CATEGORY_COLORS, CATEGORIES };
export default PostCard;
